import { useEffect, useMemo, useState } from "react";
import {
  MRT_Cell,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Grid2, IconButton } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { TeamMember } from "../../Booking-Form/typings/team-member";
import {
  renderBookingType,
  renderRequestedDate
} from "../../../components/BookingRequest/booking-requests-management.partial";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { namePerType } from "../../../components/FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { filterMRTEndDateProps } from "../../Approval/booking-approval-hook.functions";
import { BookingRequestsActiveRow } from "../../../components/BookingRequest/typings/booking-requests.types";
import { commonLocalization } from "../../../functions/tableLocalization";
import { ListTopToolbar } from "../../../components/Common/ListTablePartial/List-top-toolbar.partial";
import type { ScheduleExcelExportFetchConfig } from "../../../components/Schedule/ScheduleCalendar/functions/schedule-excel-export-fetch.functions";
import { ScheduleExcelExport } from "../../../components/Schedule/ScheduleCalendar/partial/schedule-excel-export.component";
import {
  ScheduleDataStatusIndicator,
  type ScheduleDataStatus
} from "./ScheduleDataStatusIndicator.component";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";

type P = {
  schedules: BookingScheduleInterface[];
  isLoading: boolean;
  scheduleDataStatus: ScheduleDataStatus;
  refetchAllSchedule: () => void;
  users?: TeamMember[];
  onShowDetails: (schedule: BookingScheduleInterface) => void;
  /**
   * Current fetch window (same as calendar / schedule API). When both are set, refetch runs
   * so the table can load data when the date range is established without the calendar mounted.
   */
  dateRangeStart: string | undefined;
  dateRangeEnd: string | undefined;
  onDateRangeRefetch: () => void;
  /** When true, rows can be selected for bulk delete (colleague schedule table). */
  bulkSelectForDeleteEnabled?: boolean;
  /** Selected booking ids (numeric); controlled when bulk select is enabled. */
  selectedAppointmentIds?: number[];
  onSelectedAppointmentIdsChange?: (ids: number[]) => void;
  /** Per-row gate for showing the selection checkbox (e.g. future own bookings). */
  canSelectScheduleForBulkDelete?: (schedule: BookingScheduleInterface) => boolean;
  /** Prefills export dialog when opened from the table toolbar (same window as schedule fetch). */
  excelExportInitialRange?: { startDate: string; endDate: string } | null;
  excelExportFetchConfig: ScheduleExcelExportFetchConfig;
};

/** Some schedule payloads omit `id`; MRT needs a stable string row id. */
export function scheduleTableRowId(schedule: BookingScheduleInterface): string {
  if (schedule.id != null) {
    return String(schedule.id);
  }
  return [
    schedule.bookingInventoryId,
    schedule.startDate,
    schedule.endDate,
    schedule.userBookedFor ?? ""
  ].join("|");
}

function rowSelectionFromAppointmentIds(
  schedules: BookingScheduleInterface[],
  ids: number[]
): MRT_RowSelectionState {
  const idSet = new Set(ids);
  const next: MRT_RowSelectionState = {};
  for (const sch of schedules) {
    if (sch.id != null && idSet.has(sch.id)) {
      next[scheduleTableRowId(sch)] = true;
    }
  }
  return next;
}

export function appointmentIdsFromRowSelection(
  schedules: BookingScheduleInterface[],
  rowSelection: MRT_RowSelectionState
): number[] {
  const ids: number[] = [];
  for (const sch of schedules) {
    const key = scheduleTableRowId(sch);
    if (rowSelection[key] && sch.id != null) {
      ids.push(sch.id);
    }
  }
  return ids;
}

export function getTeamMemberDisplayName(user: TeamMember, t: (k: string) => string): string {
  const displayName = [user.firstName, user.surname].filter(Boolean).join(" ").trim();
  return displayName || t("No Name");
}

export function getScheduleBookedForDisplayName({
  schedule,
  usersById,
  t
}: {
  schedule: BookingScheduleInterface;
  usersById: Map<string, TeamMember>;
  t: (k: string) => string;
}): string {
  const userBookedFor = schedule.userBookedFor ?? "";
  if (!userBookedFor) {
    return "";
  }

  if (schedule.external || schedule.externalBooking || schedule.isGuest) {
    return schedule.externalName?.trim() || schedule.externalEmail?.trim() || t("Visitor");
  }

  const user = usersById.get(userBookedFor);
  if (user) {
    return getTeamMemberDisplayName(user, t);
  }

  return userBookedFor;
}

export function getScheduleBookingObjectDisplayName(
  schedule: BookingScheduleInterface,
  t: (k: string) => string
): string {
  const name = schedule.bookingInventory?.name ?? "";
  if (name === "Mobile Working" || isScheduleMobileWorkingLocation(schedule)) {
    return t("Mobile Working");
  }
  return name;
}

export function getScheduleLocationDisplayName(schedule: BookingScheduleInterface): string {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return "";
  }
  return schedule.locationName ?? "";
}

export function getScheduleFloorDisplayName(schedule: BookingScheduleInterface): string {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return "";
  }
  return schedule.floorName ?? "";
}

export type ScheduleTableRow = BookingScheduleInterface & {
  bookedForDisplayName: string;
  bookingObjectDisplayName: string;
  locationDisplayName: string;
  floorDisplayName: string;
};

export function mapSchedulesToTableRows(
  schedules: BookingScheduleInterface[],
  usersById: Map<string, TeamMember>,
  t: (k: string) => string
): ScheduleTableRow[] {
  return schedules.map(schedule => ({
    ...schedule,
    bookedForDisplayName: getScheduleBookedForDisplayName({ schedule, usersById, t }),
    bookingObjectDisplayName: getScheduleBookingObjectDisplayName(schedule, t),
    locationDisplayName: getScheduleLocationDisplayName(schedule),
    floorDisplayName: getScheduleFloorDisplayName(schedule)
  }));
}

const renderScheduleExpandButton = ({
  row,
  onShowDetails
}: {
  row: BookingScheduleInterface;
  onShowDetails: (schedule: BookingScheduleInterface) => void;
}) => (
  <IconButton
    onClick={() => onShowDetails(row)}
    data-testid={`schedule-show-details-${scheduleTableRowId(row)}`}
  >
    <MoreVert />
  </IconButton>
);

export function ScheduleTableView({
  schedules,
  isLoading,
  scheduleDataStatus,
  refetchAllSchedule,
  users,
  onShowDetails,
  dateRangeStart,
  dateRangeEnd,
  onDateRangeRefetch,
  bulkSelectForDeleteEnabled = false,
  selectedAppointmentIds = [],
  onSelectedAppointmentIdsChange,
  canSelectScheduleForBulkDelete,
  excelExportInitialRange = null,
  excelExportFetchConfig
}: Readonly<P>) {
  const { t, i18n } = useTranslation();
  const usersById = useMemo(() => new Map((users ?? []).map(user => [user.userId, user])), [users]);

  const [tableData, setTableData] = useState<ScheduleTableRow[]>(() =>
    mapSchedulesToTableRows(schedules, usersById, t)
  );

  useEffect(() => {
    setTableData(mapSchedulesToTableRows(schedules, usersById, t));
  }, [schedules, usersById, t, i18n.language]);

  useEffect(() => {
    if (dateRangeStart == null || dateRangeEnd == null) {
      return;
    }
    onDateRangeRefetch();
  }, [dateRangeStart, dateRangeEnd, onDateRangeRefetch]);

  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = dateFormat + " " + timeFormat;

  const bookingTypeFilterSelectOptions = useMemo(
    () =>
      Array.from(new Set(schedules.map(schedule => schedule.bookingType))).map(type => ({
        label: namePerType(type as BookingType, t),
        value: type
      })),
    [schedules, t]
  );

  const columns = useMemo<MRT_ColumnDef<ScheduleTableRow>[]>(
    () => [
      {
        accessorKey: "bookingType",
        header: t("_bookingRequests_header_bookingType"),
        size: 100,
        filterVariant: "multi-select",
        filterSelectOptions: bookingTypeFilterSelectOptions,
        Cell: ({ row }) =>
          renderBookingType({ bookingType: row.original.bookingType as BookingType })
      },
      {
        accessorKey: "startDate",
        header: t("_bookingRequests_header_requestedStart"),
        size: 150,
        sortingFn: "datetime",
        filterVariant: "date",
        filterFn: "greaterThan",
        accessorFn: row => new Date(row.startDate),
        Cell: ({ cell }) =>
          renderRequestedDate(
            cell as unknown as MRT_Cell<BookingRequestsActiveRow, unknown>,
            localeFullFormat
          ),
        muiFilterDatePickerProps: {
          format: dateFormat.toUpperCase()
        }
      },
      {
        accessorKey: "endDate",
        header: t("_bookingRequests_header_requestedEnd"),
        size: 150,
        sortingFn: "datetime",
        filterVariant: "date",
        filterFn: "lessThan",
        accessorFn: row => new Date(row.endDate),
        Cell: ({ cell }) =>
          renderRequestedDate(
            cell as unknown as MRT_Cell<BookingRequestsActiveRow, unknown>,
            localeFullFormat
          ),
        muiFilterDatePickerProps: ({ column }) =>
          /* istanbul ignore next */ filterMRTEndDateProps(column, dateFormat)
      },
      {
        accessorKey: "subject",
        header: t("_bookingRequests_header_title"),
        accessorFn: row => row.subject ?? row.tripReason ?? "",
        enableSorting: false
      },
      {
        accessorKey: "bookingObjectDisplayName",
        header: t("_bookingRequests_header_bookingObject")
      },
      {
        accessorKey: "bookedForDisplayName",
        header: t("_bookingRequests_header_userInfo"),
        size: 100
      },
      {
        accessorKey: "locationDisplayName",
        header: t("Location")
      },
      {
        accessorKey: "floorDisplayName",
        header: t("Floor")
      },
      {
        accessorKey: "id",
        header: t("Show Details"),
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => renderScheduleExpandButton({ row: row.original, onShowDetails })
      }
    ],
    [bookingTypeFilterSelectOptions, i18n.language, onShowDetails, t]
  );

  const rowSelectionState = useMemo(
    () => rowSelectionFromAppointmentIds(schedules, selectedAppointmentIds),
    [schedules, selectedAppointmentIds]
  );

  const seeditMrtOptions = useSeeditMrtTableOptions<ScheduleTableRow>();

  const scheduleTable = useMaterialReactTable({
    ...seeditMrtOptions,
    columns,
    data: tableData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    enableRowSelection: bulkSelectForDeleteEnabled
      ? row =>
          Boolean(
            onSelectedAppointmentIdsChange &&
              (canSelectScheduleForBulkDelete
                ? canSelectScheduleForBulkDelete(row.original)
                : row.original.id != null)
          )
      : false,
    enableColumnActions: true,
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
      /** Newest booking start first (same as descending by start date). */
      sorting: [{ id: "startDate", desc: false }]
    }),
    state: {
      isLoading,
      ...(bulkSelectForDeleteEnabled && onSelectedAppointmentIdsChange
        ? { rowSelection: rowSelectionState }
        : {})
    },
    ...(bulkSelectForDeleteEnabled && onSelectedAppointmentIdsChange
      ? {
          onRowSelectionChange: updater => {
            const next = typeof updater === "function" ? updater(rowSelectionState) : updater;
            onSelectedAppointmentIdsChange(appointmentIdsFromRowSelection(schedules, next));
          }
        }
      : {}),
    getRowId: row => scheduleTableRowId(row),
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15],
      variant: "outlined"
    },
    localization: { ...commonLocalization(t), noRecordsToDisplay: t("_scheduleTable_noRecords") },
    renderTopToolbar: ({ table }) => (
      <ListTopToolbar
        table={table as unknown as MRT_TableInstance<Record<string, unknown>>}
        createBtnLabel={""}
        isEditable={false}
        disabledDeleteButton={true}
        onDelete={() => {}}
        importExportNode={
          <Grid2 display={"flex"} data-testid="schedule-calendar-top-container-btn-items2">
            <Grid2>
              <ScheduleExcelExport
                isDisabled={scheduleDataStatus !== "success"}
                users={users}
                fetchConfig={excelExportFetchConfig}
                initialExportRange={excelExportInitialRange}
              />
            </Grid2>
            <Grid2>
              <ScheduleDataStatusIndicator
                scheduleDataStatus={scheduleDataStatus}
                refetchAllSchedule={refetchAllSchedule}
              />
            </Grid2>
          </Grid2>
        }
      />
    )
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MaterialReactTable table={scheduleTable} />
    </LocalizationProvider>
  );
}
